<!-- GeneralSpreadSelectorLive.vue -->
<template>
  <div class="outcome-box specifier-box-wrapper" ref="wrapper">
    <button
      class="specifier-trigger"
      :class="[getSpecifierType(resolvedMarket.specifier), { 'no-alternatives': !hasLines }]"
      @click.stop="hasLines ? toggleDropdown() : null"
    >
      <span class="trigger-label">{{ stripScoreState(resolvedMarket.specifier) }}</span>
      <svg v-if="hasLines" class="trigger-chevron" :class="{ open: showDropdown }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </button>

    <transition name="spec-drop">
      <div v-if="showDropdown" class="spec-dropdown" :class="{ 'drop-up': dropUp }" :style="dropStyle">
        <div
          v-for="(line, lIdx) in alternativeLines"
          :key="lIdx"
          class="spec-row"
          @mousedown.prevent="handleChange(line.specifier)"
        >
          <span class="spec-badge" :class="getSpecifierType(line.specifier)">
            {{ stripScoreState(line.specifier) }}
          </span>

          <div class="spec-odds">
            <template v-for="(o, i) in line.outcome.slice(0, 3)">
              <button
                v-if="isOddPlayable(o)"
                :key="i"
                class="spec-odd-btn"
                :class="{ 'odd-selected': oddsSources.includes(o.unique_id) }"
                @mousedown.stop="handlePutCart(line, o)"
              >
                <span class="spec-odd-name">{{ o.outcome_name }}</span>
                <span class="spec-odd-val">{{ o.outcome_odd_value | odds_type_filter }}</span>
              </button>
              <span v-else :key="'lock_'+i" class="spec-lock-wrap">
                <svg class="spec-lock" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </span>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'GeneralSpreadSelectorLive',
  props: {
    event:              { type: Object,   required: true },
    marketDef:          { type: Object,   required: true },
    resolvedMarket:     { type: Object,   required: true },
    getAlternativeLines:{ type: Function, required: true },
    changeLine:         { type: Function, required: true },
    isOddPlayable:      { type: Function, required: true },
    getSpecifierType:   { type: Function, required: true },
    stripScoreState:    { type: Function, required: true },
    oddsSources:        { type: Array,    default: () => [] },
  },
  data() {
    return { showDropdown: false, dropUp: false, dropStyle: {} };
  },
  computed: {
    alternativeLines() {
      return this.getAlternativeLines(this.event, this.resolvedMarket);
    },
    hasLines() {
      return this.resolvedMarket.specifier != null && this.alternativeLines.length > 0;
    },
  },
  mounted() {
    this._closeOnOutside = (e) => {
      if (!this.$refs.wrapper || !this.$refs.wrapper.contains(e.target)) {
        this.showDropdown = false;
      }
    };
    this._closeOnScroll = () => {
      if (this.showDropdown) this.showDropdown = false;
    };
    document.addEventListener('mousedown', this._closeOnOutside);
    window.addEventListener('scroll', this._closeOnScroll, true);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this._closeOnOutside);
    window.removeEventListener('scroll', this._closeOnScroll, true);
  },
  methods: {
    toggleDropdown() {
      if (!this.showDropdown) {
        const rect = this.$refs.wrapper.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = 260;
        this.dropUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
        if (this.dropUp) {
          this.dropStyle = {
            top: 'auto',
            bottom: (window.innerHeight - rect.top + 4) + 'px',
            left: rect.left + 'px',
          };
        } else {
          this.dropStyle = {
            top: (rect.bottom + 4) + 'px',
            bottom: 'auto',
            left: rect.left + 'px',
          };
        }
      }
      this.showDropdown = !this.showDropdown;
    },
    handleChange(specifier) {
      this.changeLine(this.event, this.marketDef, specifier);
      this.showDropdown = false;
    },
    handlePutCart(market, odd) {
      this.$emit('put-cart', { market, odd });
      this.showDropdown = false;
    },
  }
}
</script>

<style scoped>
.outcome-box.specifier-box-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── TRIGGER BUTTON ── */
.specifier-trigger {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0 6px;
  height: 100%;
  border-radius: 4px;
  border: 1px solid var(--bg-card-hover);
  color: var(--text-platform);
  font-size: var(--font-size-xl);
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  touch-action: manipulation;
  width: 100%;
}

.trigger-label { flex: 1; }
.trigger-chevron {
  flex-shrink: 0;
  opacity: 0.7;
  transition: transform 0.2s cubic-bezier(.34,1.56,.64,1);
}
.trigger-chevron.open { transform: rotate(180deg); }
.specifier-trigger.no-alternatives { cursor: default; }

/* ── DROPDOWN PANEL ── */
.spec-dropdown {
  position: fixed;
  z-index: 99999;
  min-width: 240px;
  max-height: 260px;
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--bg-card, #252525);
  border: 1px solid var(--border-color, #3a3a3a);
  border-radius: 4px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.55), 0 2px 8px rgba(0, 0, 0, 0.3);
  scrollbar-width: none;
}
.spec-dropdown::-webkit-scrollbar { display: none; }

/* ── DROPDOWN ROWS ── */
.spec-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  border-bottom: 1px solid var(--border-color, #2e2e2e);
  transition: background 0.12s;
}
.spec-row:last-child { border-bottom: none; }
.spec-row:hover      { background: rgba(255,255,255,0.04); }

.spec-badge {
  flex-shrink: 0;
  font-size: var(--font-size-xl);
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 4px;
  background: rgba(255,255,255,0.06);
  color: var(--text-platform);
  min-width: 36px;
  text-align: center;
}

/* ── ODD BUTTONS ── */
.spec-odds {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
}

.spec-odd-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  min-width: 64px;
  height: 36px;
  padding: 0 6px;
  border-radius: 6px;
  border: 1px solid var(--border-color, #3a3a3a);
  background: var(--bg-body, #1a1a1a);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  touch-action: manipulation;
}
.spec-odd-btn:hover {
  background: var(--bg-cell-hover, #2a2a2a);
  border-color: var(--color-primary-light, #4a7a5a);
}
.spec-odd-btn:active { opacity: 0.75; }
.spec-odd-btn.odd-selected {
  background: var(--color-primary-gradient);
  border-color: var(--color-primary-light-op-50);
  border-bottom-color: var(--color-primary-light);
}

.spec-odd-name {
  font-size: var(--font-size-xs);
  color: var(--text-op-50-platform, #888);
  letter-spacing: 0.3px;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50px;
}
.spec-odd-val {
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--text-platform, #fff);
  line-height: 1;
}
.spec-odd-btn.odd-selected .spec-odd-name,
.spec-odd-btn.odd-selected .spec-odd-val { color: var(--text-platform, #fff); }

.spec-lock-wrap { display: flex; align-items: center; justify-content: center; width: 44px; height: 36px; }
.spec-lock { opacity: 0.3; flex-shrink: 0; }

/* ── TRANSITION ── */
.spec-drop-enter-active { transition: opacity 0.15s ease, transform 0.18s cubic-bezier(0.34, 1.4, 0.64, 1); }
.spec-drop-leave-active { transition: opacity 0.1s ease, transform 0.12s ease; }
.spec-drop-enter, .spec-drop-leave-to { opacity: 0; transform: translateY(-6px); }
.spec-dropdown.drop-up.spec-drop-enter,
.spec-dropdown.drop-up.spec-drop-leave-to { transform: translateY(6px); }
</style>
